// src/pages/WorkflowTemplatesPage.jsx

/**
 * Workflow Templates Page – displays public workflow templates (is_template = true)
 * and allows an authenticated user to copy a template into their own workspace.
 *
 * API endpoints (already implemented in src/api/routes/workflows.js):
 *   GET  /api/workflows/templates                     – list public templates
 *   POST /api/workflows/templates/:id/use            – copy template for current user
 *
 * The page fetches the list on mount, shows a loading spinner, and renders each
 * template with a "Use This Template" button. The button triggers the POST request;
 * on success we navigate the user to the newly created workflow (the API returns the
 * new workflow id). If the user is not logged in we simply redirect to the login
 * page.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";

export default function WorkflowTemplatesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingId, setUsingId] = useState(null);

  // Fetch public templates on mount
  useEffect(() => {
    async function fetchTemplates() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch("/api/workflows/templates", {
          credentials: "include",
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || `Server responded ${res.status}`);
        }
        const data = await res.json();
        setTemplates(data.templates || []);
      } catch (e) {
        console.error(e);
        setError(e.message || "Failed to load templates");
      } finally {
        setLoading(false);
      }
    }
    fetchTemplates();
  }, []);

  const handleUseTemplate = async (templateId) => {
    if (!user) {
      // Not logged in – redirect to login page
      navigate("/login");
      return;
    }
    setUsingId(templateId);
    try {
      const res = await fetch(`/api/workflows/templates/${templateId}/use`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        // No body needed – server knows current user from auth cookie/JWT
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Server responded ${res.status}`);
      }
      const data = await res.json();
      // Expect the API to return the new workflow id
      const newId = data.workflow?.id || data.id;
      if (newId) {
        navigate(`/workflows/${newId}`);
      } else {
        // Fallback – just refresh the list
        alert("Template copied successfully!");
      }
    } catch (e) {
      console.error(e);
      alert(`Failed to copy template: ${e.message}`);
    } finally {
      setUsingId(null);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-red-600 bg-red-50 border border-red-200 rounded-md max-w-2xl mx-auto mt-8">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Workflow Templates</h1>
      {templates.length === 0 ? (
        <p className="text-gray-500">No public templates available.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow"
            >
              <h2 className="text-lg font-medium mb-2 truncate">{tpl.title || "Untitled Template"}</h2>
              {tpl.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{tpl.description}</p>
              )}
              <button
                onClick={() => handleUseTemplate(tpl.id)}
                disabled={usingId === tpl.id}
                className={`w-full px-3 py-2 rounded-md text-white font-medium ${
                  usingId === tpl.id ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {usingId === tpl.id ? "Copying…" : "Use This Template"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
